import _upperFirst from "lodash/upperFirst";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Flex, Text, Tooltip } from 'core/components';
import CoreSelect from 'core/components/select/Select';
import { Field, InputGroup, Select } from 'core/form';
import { getMeasurementMetric, getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { useFormContext } from 'core/form/FormContext';
import { getDimensionDisplayLabel, getRollupKey } from '@kentik/ui-shared/query/query';
import { combineFilterValue, getUnitOptions, parseFilterValue } from '@kentik/ui-shared/filters/metricFilters';
import { DNS_CODE_FILTER_OPTIONS, HEALTH_FILTER_OPTIONS, HTTP_COMBINED_FILTER_OPTIONS, METRIC_OPERATORS } from './filterConstants';

/** Connector field for setting group to Any/All */

/**
 * Input types for different metric units
 */

/**
 * Determine the input type based on metric's Unit property
 */
function getMetricInputType(metricInfo) {
  if (!metricInfo?.Unit) {
    return 'numeric';
  }
  const unit = metricInfo.Unit;
  if (unit === 'health') {
    return 'health';
  }
  if (unit === 'dns_code') {
    return 'dns_code';
  }
  if (unit === 'http_status_code') {
    return 'http_status';
  }
  return 'numeric';
}

/**
 * Get available operators based on input type
 * Categorical types only support = and <>
 */
function getOperatorsForInputType(inputType) {
  if (inputType === 'numeric') {
    return METRIC_OPERATORS;
  }
  // Categorical types: only equality operators
  return [{
    value: '=',
    label: 'equals'
  }, {
    value: '<>',
    label: 'does not equal'
  }];
}

/**
 * Metric filter state - extends base filter with metric-specific fields
 */

/**
 * UDE Metric Filter Item - renders a single metric filter row
 * Allows users to filter on numeric metric values with unit conversion
 */
const UdeMetricFilterItem = observer(({
  filter,
  index,
  onRemove,
  measurement,
  measurements,
  autoFocus,
  groupFilters,
  groupConnector
}) => {
  const form = useFormContext();
  const selectedMetrics = useMemo(() => {
    if (!form) {
      return [];
    }
    return form.getValue('config.data.metrics') || [];
  }, [form]);
  const selectedRollups = useMemo(() => {
    if (!form) {
      return [];
    }
    return form.getValue('config.data.rollups') || [];
  }, [form]);

  // Get measurement object
  const measurementObj = useMemo(() => {
    if (!measurement) {
      return null;
    }
    return getMeasurementSync(measurement, measurements);
  }, [measurement, measurements]);

  // Build metric options from selected metrics
  const metricOptions = useMemo(() => {
    if (!measurementObj || selectedRollups.length === 0 && selectedMetrics.length === 0) {
      return [];
    }
    const options = [];
    selectedMetrics.forEach(m => {
      const metricInfo = getMeasurementMetric(measurementObj, m.name);
      if (metricInfo?.Aggregate) {
        options.push({
          value: m.name,
          metric: m.name,
          aggregate: '',
          label: metricInfo.Label || `${_upperFirst(metricInfo.Aggregate)} ${m.name}`
        });
      }
    });
    selectedRollups.forEach(r => {
      const metricInfo = getMeasurementMetric(measurementObj, r.metric);
      const baseLabel = metricInfo?.Label || r.metric;
      const label = metricInfo?.Window === 'count' ? getDimensionDisplayLabel(r.metric, baseLabel) : baseLabel;
      options.push({
        value: getRollupKey(r),
        metric: r.metric,
        aggregate: r.fn,
        label: `${_upperFirst(r.fn)} ${label}`
      });
    });
    return options;
  }, [measurementObj, selectedMetrics, selectedRollups]);

  // Get current metric info for unit options
  const currentMetricName = filter.metric?.getValue() ?? '';
  const currentMetricInfo = useMemo(() => {
    if (!measurementObj || !currentMetricName) {
      return undefined;
    }
    return getMeasurementMetric(measurementObj, currentMetricName) ?? undefined;
  }, [measurementObj, currentMetricName]);

  // Determine input type and available operators based on metric unit
  const inputType = useMemo(() => getMetricInputType(currentMetricInfo), [currentMetricInfo]);
  const availableOperators = useMemo(() => getOperatorsForInputType(inputType), [inputType]);

  // Get unit options based on selected metric (only for numeric input type)
  const unitOptions = useMemo(() => getUnitOptions(currentMetricInfo), [currentMetricInfo]);

  // Parse current filter value
  const {
    numericValue,
    unit
  } = parseFilterValue(filter.filterValue.getValue());

  // State for controlled unit selector
  const [selectedUnit, setSelectedUnit] = useState(() =>
  // Initialize to stored unit or first available option
  unit || (unitOptions.length > 0 ? unitOptions[0].value : ''));

  // Update selected unit when metric changes
  useEffect(() => {
    if (unitOptions.length > 0 && !unitOptions.some(opt => opt.value === selectedUnit)) {
      setSelectedUnit(unitOptions[0].value);
      // Update stored value with new unit - derive numericValue fresh to avoid stale closure
      const currentNumericValue = parseFilterValue(filter.filterValue.getValue()).numericValue;
      if (currentNumericValue) {
        filter.filterValue.setValue(combineFilterValue(currentNumericValue, unitOptions[0].value));
      }
    }
  }, [unitOptions, selectedUnit, filter.filterValue]);
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [onRemove, index]);
  const handleMetricChange = useCallback((_field, value) => {
    const selectedOption = metricOptions.find(opt => opt.value === value);
    if (selectedOption) {
      filter.metric?.setValue(selectedOption.metric);
      filter.aggregate?.setValue(selectedOption.aggregate);
    }
  }, [filter, metricOptions]);
  const handleNumericValueChange = useCallback(e => {
    const newNumericValue = e.target.value;
    filter.filterValue.setValue(combineFilterValue(newNumericValue, selectedUnit));
  }, [filter.filterValue, selectedUnit]);
  const handleUnitChange = useCallback(newUnit => {
    setSelectedUnit(newUnit);
    // Derive numericValue fresh to avoid stale closure issues
    const currentNumericValue = parseFilterValue(filter.filterValue.getValue()).numericValue;
    filter.filterValue.setValue(combineFilterValue(currentNumericValue, newUnit));
  }, [filter.filterValue]);

  // Handler for categorical single-select (health, dns_code)
  const handleCategoricalChange = useCallback(value => {
    filter.filterValue.setValue(value);
  }, [filter.filterValue]);

  // Get all HTTP filter values in the group for dimension-style multi-select
  // Don't use useMemo here - we need MobX to track the observable access
  const allHttpFiltersInGroup = [];
  if (groupFilters && inputType === 'http_status') {
    groupFilters.fieldStates.forEach((f, idx) => {
      const metricName = f.metric?.getValue?.() || '';
      if (metricName === currentMetricName) {
        const value = f.filterValue.getValue();
        if (value) {
          allHttpFiltersInGroup.push({
            index: idx,
            value
          });
        }
      }
    });
  }

  // Get all selected HTTP values across all filters in the group
  const httpSelectedValues = inputType === 'http_status' ? allHttpFiltersInGroup.map(f => f.value) : [];

  // Handler for HTTP multi-select - dimension-style: one filter per value
  const handleHttpMultiChange = useCallback(values => {
    if (!groupFilters) {
      return;
    }

    // Compute current HTTP filters fresh to avoid stale closure issues
    const metricName = currentMetricName;
    const currentHttpFilters = [];
    groupFilters.fieldStates.forEach((f, idx) => {
      const fMetricName = f.metric?.getValue?.() || '';
      if (fMetricName === metricName) {
        const value = f.filterValue.getValue();
        if (value) {
          currentHttpFilters.push({
            index: idx,
            value
          });
        }
      }
    });
    const newValues = Array.isArray(values) ? values : [values];
    const currentValues = currentHttpFilters.map(f => f.value);
    const operator = filter.operator.getValue();

    // Find values to add (in newValues but not in currentValues)
    const toAdd = newValues.filter(v => !currentValues.includes(v));

    // Find values to remove (in currentValues but not in newValues)
    const toRemove = currentHttpFilters.filter(f => !newValues.includes(f.value));

    // Remove filters (in reverse order to maintain indices)
    toRemove.sort((a, b) => b.index - a.index).forEach(f => {
      groupFilters.remove(f.index);
    });

    // For the first value, if this filter is empty, set it instead of adding
    if (toAdd.length > 0 && !filter.filterValue.getValue()) {
      filter.filterValue.setValue(toAdd[0]);
      filter.metric?.setValue(metricName);
      filter.filterField.setValue(metricName);
      toAdd.shift(); // Remove first since we used it
    }

    // Add new filters for remaining values
    toAdd.forEach(value => {
      groupFilters.add({
        metric: metricName,
        aggregate: '',
        filterField: metricName,
        operator,
        filterValue: value
      });
    });

    // Set group connector to "Any" when multiple HTTP filters exist (OR logic)
    const totalHttpFilters = currentHttpFilters.length - toRemove.length + toAdd.length;
    if (groupConnector && totalHttpFilters > 1) {
      groupConnector.setValue('Any');
    }
  }, [groupFilters, currentMetricName, filter, groupConnector]);

  // Sync operator changes across all HTTP filters in the group
  // When user changes operator on the visible filter, apply to all hidden ones too
  const currentOperator = filter.operator.getValue();
  useEffect(() => {
    if (inputType !== 'http_status' || !groupFilters) {
      return;
    }

    // Compute HTTP filters fresh inside effect to avoid stale closure issues
    // and to avoid lint warning about array dependency changing every render
    let firstHttpIndex = -1;
    groupFilters.fieldStates.forEach((f, idx) => {
      const fMetricName = f.metric?.getValue?.() || '';
      if (fMetricName === currentMetricName && f.filterValue.getValue()) {
        if (firstHttpIndex === -1) {
          firstHttpIndex = idx;
        }
      }
    });

    // Only sync if this is the first (visible) HTTP filter
    if (firstHttpIndex !== -1 && firstHttpIndex !== index) {
      return;
    }

    // Update all other HTTP filters to match this operator
    groupFilters.fieldStates.forEach((f, idx) => {
      if (idx === index) {
        return;
      } // Skip self
      const fMetricName = f.metric?.getValue?.() || '';
      if (fMetricName === currentMetricName) {
        const existingOperator = f.operator.getValue();
        if (existingOperator !== currentOperator) {
          f.operator.setValue(currentOperator);
        }
      }
    });
  }, [currentOperator, inputType, groupFilters, currentMetricName, index]);

  // Build unit suffix options for Select
  const unitSelectOptions = useMemo(() => unitOptions.map(opt => ({
    value: opt.value,
    label: opt.value || '(raw)'
  })), [unitOptions]);
  const hasNoMetrics = metricOptions.length === 0;
  const hasMetricField = !!filter.metric;

  // For HTTP status filters, only render the first one in the group
  // The multi-select will show all values, but we hide duplicate rows
  if (inputType === 'http_status' && allHttpFiltersInGroup.length > 0) {
    const firstHttpIndex = allHttpFiltersInGroup[0].index;
    if (index !== firstHttpIndex) {
      return null;
    }
  }

  // Render metric selector content based on state
  const renderMetricSelector = () => {
    if (hasNoMetrics) {
      return /*#__PURE__*/React.createElement(Text, {
        small: true,
        muted: true,
        p: 1
      }, "No units selected");
    }
    if (!hasMetricField) {
      return /*#__PURE__*/React.createElement(Text, {
        small: true,
        muted: true,
        p: 1
      }, "Loading...");
    }
    return /*#__PURE__*/React.createElement(Field, {
      field: filter.filterField,
      showLabel: false,
      mb: 0,
      options: metricOptions,
      onChange: handleMetricChange
    }, /*#__PURE__*/React.createElement(Select, {
      small: true,
      fill: true,
      placeholder: "Select unit..."
    }));
  };
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-start",
    gap: 1,
    p: 1,
    borderBottom: "thin"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1 1 150px",
    minWidth: 0
  }, renderMetricSelector()), /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 120px"
  }, /*#__PURE__*/React.createElement(Field, {
    field: filter.operator,
    showLabel: false,
    mb: 0,
    options: availableOperators
  }, /*#__PURE__*/React.createElement(Select, {
    small: true,
    fill: true
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1 1 180px",
    minWidth: 0
  }, inputType === 'health' && /*#__PURE__*/React.createElement(CoreSelect, {
    small: true,
    fill: true,
    options: HEALTH_FILTER_OPTIONS,
    values: filter.filterValue.getValue(),
    onChange: handleCategoricalChange,
    disabled: !currentMetricName,
    placeholder: "Select health status..."
  }), inputType === 'dns_code' && /*#__PURE__*/React.createElement(CoreSelect, {
    small: true,
    fill: true,
    options: DNS_CODE_FILTER_OPTIONS,
    values: filter.filterValue.getValue(),
    onChange: handleCategoricalChange,
    disabled: !currentMetricName,
    placeholder: "Select DNS code..."
  }), inputType === 'http_status' && /*#__PURE__*/React.createElement(CoreSelect, {
    small: true,
    fill: true,
    multi: true,
    keepOpen: true,
    toggle: true,
    options: HTTP_COMBINED_FILTER_OPTIONS,
    values: httpSelectedValues,
    onChange: handleHttpMultiChange,
    disabled: !currentMetricName,
    placeholder: "Select HTTP status..."
  }), inputType === 'numeric' && /*#__PURE__*/React.createElement(Flex, {
    gap: "2px"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: 1
  }, /*#__PURE__*/React.createElement(InputGroup, {
    small: true,
    fill: true,
    type: "number",
    placeholder: "Value...",
    value: numericValue,
    onChange: handleNumericValueChange,
    autoFocus: autoFocus && !!currentMetricName,
    disabled: !currentMetricName
  })), unitOptions.length > 1 && /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 70px"
  }, /*#__PURE__*/React.createElement(CoreSelect, {
    small: true,
    fill: true,
    options: unitSelectOptions,
    values: selectedUnit,
    onChange: handleUnitChange,
    disabled: !currentMetricName
  })), unitOptions.length === 1 && unitOptions[0].value && /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 auto",
    alignSelf: "center",
    px: 1
  }, /*#__PURE__*/React.createElement(Text, {
    small: true,
    muted: true
  }, unitOptions[0].value)))), /*#__PURE__*/React.createElement(Tooltip, {
    content: "Remove filter",
    placement: "top"
  }, /*#__PURE__*/React.createElement(Button, {
    icon: "small-cross",
    minimal: true,
    small: true,
    intent: "danger",
    onClick: handleRemove
  })));
});
export default UdeMetricFilterItem;